import { createAsyncThunk } from '@reduxjs/toolkit';
import type {
  HeaderInfo,
  RtCond,
  RtCondEx,
} from '@travel/design-system/core/src/components/ProviderCommonHeader/types';
import type { ApiClient } from '@travel/api-client';
import { apiPath } from 'constants/api';
import type { RootState } from 'react-redux';
import { parseRTCondCookie } from 'utils/cookie';
import { noticeError } from '@travel/tracker';
import loggerGenerator from '@travel/logger';

const logger = loggerGenerator();

export const fetchHeaderInfo = createAsyncThunk<
  { data: HeaderInfo; rtCond: RtCond; rtCondEx: RtCondEx },
  number,
  { state: RootState; extra: { apiClient: ApiClient } }
>(
  'providerCommonHeader/fetchHeaderInfo',
  async (providerId, { getState, extra: { apiClient }, rejectWithValue }) => {
    try {
      const normalizedCookies = getState()._httpRequest.normalizedCookies;
      const rtCond: RtCond = parseRTCondCookie(normalizedCookies['RTCOND']?.value ?? '');
      const rtCondExCookie = normalizedCookies['RTCOND_EX'];

      const rtCondEx = normalizedCookies['RTCOND_EX']
        ? JSON.parse(rtCondExCookie.value)
        : {
            dated: false,
            dayuse: false,
          };

      const data: HeaderInfo = await apiClient.get(apiPath.commonHeader.pathname(providerId), {
        values: rtCond,
        ignoreCommonErrorHandler: true,
      });

      return { data, rtCond, rtCondEx };
    } catch (error) {
      logger.error('Provider Common Header Fetch Header Info Error', error);
      noticeError('Provider Common Header Fetch Header Info Error', error);

      return rejectWithValue(error);
    }
  },
);

export const fetchFavoriteListStatus = createAsyncThunk<
  { providerId: string; planId: string | null; bookmarked: boolean }[],
  string | undefined,
  { extra: { apiClient: ApiClient } }
>(
  'providerCommonHeader/fetchFavoriteListStatus',
  async (providerId, { extra: { apiClient }, rejectWithValue }) => {
    try {
      if (!providerId) {
        return [];
      }

      const data: { providerId: string; planId: string | null; bookmarked: boolean }[] =
        await apiClient.post(apiPath.getFavoriteListStatus.pathname, {
          values: {
            checkTargets: [
              {
                providerId: providerId,
              },
            ],
            entityType: 'PROVIDER',
            serviceType: 'DOMESTIC',
          },
          ignoreCommonErrorHandler: true,
          customParams: {
            isTravelApi: true,
          },
        });

      return data;
    } catch (error) {
      logger.error('Fetch Bookmark Status Failed', error);
      noticeError('Fetch Bookmark Status Failed', error);

      return rejectWithValue(error);
    }
  },
);

export const addToFavoriteList = createAsyncThunk<
  void,
  void,
  { state: RootState; extra: { apiClient: ApiClient } }
>(
  'providerCommonHeader/addToFavoriteList',
  async (_, { getState, extra: { apiClient }, rejectWithValue }) => {
    try {
      const providerId: string = getState().providerCommonHeader.data.hotel.hotelNo;

      await apiClient.post(apiPath.favoriteList.pathname, {
        values: {
          serviceType: 'DOMESTIC',
          providerId: providerId,
          limit: 3000,
        },
        ignoreCommonErrorHandler: true,
        customParams: {
          isTravelApi: true,
        },
      });
    } catch (error) {
      noticeError('Add to Bookmark Failed', error);

      return rejectWithValue(error);
    }
  },
);

export const deleteFromFavoriteList = createAsyncThunk<
  void,
  void,
  { state: RootState; extra: { apiClient: ApiClient } }
>(
  'providerCommonHeader/deleteFromFavoriteList',
  async (_, { getState, extra: { apiClient }, rejectWithValue }) => {
    try {
      const providerId: string = getState().providerCommonHeader.data.hotel.hotelNo;

      await apiClient.delete(apiPath.favoriteList.pathname, {
        values: {
          serviceType: 'DOMESTIC',
          providerId: providerId,
        },
        ignoreCommonErrorHandler: true,
        customParams: {
          isTravelApi: true,
        },
      });
    } catch (error) {
      noticeError('Delete from Bookmark Failed', error);

      return rejectWithValue(error);
    }
  },
);
